// Dashboard 4 — Send-Time Intelligence. Two heatmaps: by hour-of-day (0..23,
// UTC) and by day-of-week. Cells shade by conversion intensity; engagement_rate
// may be null for unmeasured channels → shown as N/A.

import React from "react";
import { Skeleton } from "../../../ui/skeleton";
import { Panel } from "../../../Components/admin/reporting/controls";
import { getEngagementSendTime } from "../../../Services/adminEngagement";
import {
  Rate,
  ErrorBox,
  EmptyState,
  WindowBadge,
  fmtInt,
  fmtPctValue,
  useEngagementData,
} from "./engagementShared";

// Sky-blue intensity scale; null intensity → neutral "N/A" grey.
const cellStyle = (intensity) => {
  if (intensity == null) {
    return { background: "repeating-linear-gradient(45deg,#f1f5f9,#f1f5f9 4px,#e2e8f0 4px,#e2e8f0 8px)", color: "#94a3b8" };
  }
  const t = Math.max(0, Math.min(1, intensity));
  return {
    backgroundColor: `rgba(37,99,235,${0.08 + 0.85 * t})`,
    color: t > 0.55 ? "#fff" : "#1e293b",
  };
};

const SendTimeTab = ({ range }) => {
  const { data, loading, error } = useEngagementData(
    () => getEngagementSendTime(range),
    [range]
  );

  const tz = data?.timezone || "UTC";
  const byHour = data?.by_hour || [];
  const byDay = data?.by_day_of_week || [];
  const maxHourConv = Math.max(...byHour.map((h) => h.conversion_pct || 0), 0.0001);
  const maxDayConv = Math.max(...byDay.map((d) => d.conversion_rate || 0), 0.0001);

  if (error) return <ErrorBox>{error}</ErrorBox>;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-3">
        <WindowBadge window={data?.window} timezone={tz} />
        <span className="text-xs text-muted-foreground">
          Buckets are in {tz}. Cells shade by conversion intensity.
        </span>
      </div>

      <Panel title={`By hour of day (${tz})`}>
        {loading && !data ? (
          <Skeleton className="h-24 w-full" />
        ) : byHour.length === 0 ? (
          <EmptyState>No send-time data in this window.</EmptyState>
        ) : (
          <div className="grid grid-cols-12 gap-1.5">
            {byHour.map((h) => (
              <div
                key={h.hour}
                className="flex aspect-square flex-col items-center justify-center rounded-md text-[11px] font-medium tabular-nums transition-transform hover:scale-105"
                style={cellStyle((h.conversion_pct || 0) / maxHourConv)}
                title={`${String(h.hour).padStart(2, "0")}:00 ${tz} · ${fmtInt(
                  h.messages_sent
                )} sent · ${fmtInt(h.opens)} opens · ${fmtInt(
                  h.clicks
                )} clicks · ${fmtInt(h.sessions_started)} sessions · ${fmtPctValue(
                  h.conversion_pct
                )} conv`}
              >
                <span className="opacity-80">{String(h.hour).padStart(2, "0")}</span>
                <span className="font-semibold">{fmtPctValue(h.conversion_pct, 0)}</span>
              </div>
            ))}
          </div>
        )}
      </Panel>

      <Panel title={`By day of week (${tz})`}>
        {loading && !data ? (
          <Skeleton className="h-24 w-full" />
        ) : byDay.length === 0 ? (
          <EmptyState>No day-of-week data in this window.</EmptyState>
        ) : (
          <div className="grid grid-cols-7 gap-2">
            {byDay.map((d) => (
              <div
                key={d.day}
                className="rounded-lg border border-slate-200 p-3 text-center"
                style={cellStyle((d.conversion_rate || 0) / maxDayConv)}
                title={`${d.day} · ${fmtInt(d.messages_sent)} sent`}
              >
                <div className="text-xs font-semibold">{d.day?.slice(0, 3)}</div>
                <div className="mt-1 text-base font-bold tabular-nums">
                  {d.conversion_rate == null
                    ? "—"
                    : `${Math.round(d.conversion_rate * 100)}%`}
                </div>
                <div className="text-[10px] opacity-80">conversion</div>
                <div className="mt-1.5 text-[11px]">
                  Eng:{" "}
                  <Rate
                    value={d.engagement_rate}
                    available={d.engagement_rate != null}
                    hint="engagement"
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </Panel>
    </div>
  );
};

export default SendTimeTab;
